import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Trash, MapPin } from "@phosphor-icons/react";
import { EVENT_TYPES } from "../../api/planning";
import Modal from "../ui-shared/Modal";
import { toLocalInput, fromLocalInput } from "./calendarUtils";

const FIELD =
  "rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white w-full";
const LABEL =
  "block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5";

const blank = (initialDate) => {
  const start = initialDate || new Date();
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    title: "",
    type: "meeting",
    start_at: toLocalInput(start.toISOString()),
    end_at: toLocalInput(end.toISOString()),
    location: "",
    description: "",
    attendees_text: "",
    visible_to_model: true,
  };
};

export default function PlanningEventModal({
  open,
  onClose,
  initialEvent,
  initialDate,
  onSave,
  onDelete,
  canTogglevisibility = true,
}) {
  const { t } = useTranslation();
  const [data, setData] = useState(blank(initialDate));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initialEvent) {
      setData({
        title: initialEvent.title || "",
        type: initialEvent.type || "custom",
        start_at: toLocalInput(initialEvent.start_at),
        end_at: toLocalInput(initialEvent.end_at),
        location: initialEvent.location || "",
        description: initialEvent.description || "",
        attendees_text: (initialEvent.attendees || []).join(", "),
        visible_to_model: initialEvent.visible_to_model !== false,
      });
    } else {
      setData(blank(initialDate));
    }
    setError("");
  }, [open, initialEvent, initialDate]);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      if (!data.title.trim()) {
        setError(t("planning.errors.title_required"));
        return;
      }
      const startIso = fromLocalInput(data.start_at);
      const endIso = fromLocalInput(data.end_at);
      if (!startIso || !endIso) {
        setError(t("planning.errors.date_required"));
        return;
      }
      if (new Date(endIso) < new Date(startIso)) {
        setError(t("planning.errors.end_after_start"));
        return;
      }
      const payload = {
        title: data.title.trim(),
        type: data.type,
        start_at: startIso,
        end_at: endIso,
        location: data.location.trim() || null,
        description: data.description.trim() || null,
        attendees: data.attendees_text
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        visible_to_model: !!data.visible_to_model,
      };
      setSaving(true);
      try {
        await onSave(payload);
        onClose?.();
      } catch (err) {
        setError(err?.message || t("planning.errors.generic"));
      } finally {
        setSaving(false);
      }
    },
    [data, onSave, onClose, t],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        initialEvent
          ? t("planning.modal.title_edit")
          : t("planning.modal.title_new")
      }
    >
      <form onSubmit={submit} className="space-y-3" data-testid="event-form">
        <div>
          <label className={LABEL}>{t("planning.field.title")}</label>
          <input
            data-testid="event-title"
            className={FIELD}
            value={data.title}
            onChange={(e) => setField("title", e.target.value)}
            autoFocus
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>{t("planning.field.type")}</label>
            <select
              data-testid="event-type"
              className={FIELD}
              value={data.type}
              onChange={(e) => setField("type", e.target.value)}
            >
              {EVENT_TYPES.map((tp) => (
                <option key={tp} value={tp}>
                  {t(`planning.types.${tp}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL}>
              <MapPin size={11} weight="regular" className="inline mr-1" />
              {t("planning.field.location")}
            </label>
            <input
              data-testid="event-location"
              className={FIELD}
              value={data.location}
              onChange={(e) => setField("location", e.target.value)}
              placeholder={t("planning.field.location_placeholder")}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>{t("planning.field.start_at")}</label>
            <input
              data-testid="event-start"
              type="datetime-local"
              className={FIELD}
              value={data.start_at}
              onChange={(e) => setField("start_at", e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL}>{t("planning.field.end_at")}</label>
            <input
              data-testid="event-end"
              type="datetime-local"
              className={FIELD}
              value={data.end_at}
              onChange={(e) => setField("end_at", e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className={LABEL}>{t("planning.field.attendees")}</label>
          <input
            data-testid="event-attendees"
            className={FIELD}
            value={data.attendees_text}
            onChange={(e) => setField("attendees_text", e.target.value)}
            placeholder={t("planning.field.attendees_placeholder")}
          />
        </div>

        <div>
          <label className={LABEL}>{t("planning.field.description")}</label>
          <textarea
            data-testid="event-description"
            rows={3}
            className={FIELD + " resize-y"}
            value={data.description}
            onChange={(e) => setField("description", e.target.value)}
          />
        </div>

        {canTogglevisibility && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="event-visible-toggle"
              checked={data.visible_to_model}
              onChange={(e) => setField("visible_to_model", e.target.checked)}
            />
            {t("planning.field.visible_to_model")}
          </label>
        )}

        {error && (
          <p data-testid="event-form-error" className="text-xs text-red-600">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          <div>
            {initialEvent && onDelete && (
              <button
                type="button"
                data-testid="event-delete"
                onClick={async () => {
                  await onDelete(initialEvent.id);
                  onClose?.();
                }}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash size={13} weight="regular" />
                {t("common.delete")}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              data-testid="event-submit"
              disabled={saving}
              className="rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
